/**
 * Custom Feature Users Page Component
 * 
 * This component handles the /admin/features/{feature}/users route pattern
 * and makes API calls directly to the correct endpoints as specified in the
 * global feature management documentation.
 * 
 * API Endpoints Used:
 * - GET /features/{feature}/users - List users for feature
 * - POST /features/{feature}/users - Add user to feature  
 * - POST /features/{feature}/users/bulk - Bulk add via CSV
 * - DELETE /features/{feature}/users/{id} - Remove user from feature
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { stringify } from 'qs';
import httpClient from '../utils/httpClient';
import {
} from 'react-admin';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Public,
  GetApp,
  Add as AddIcon,
  ArrowBack,
} from '@mui/icons-material';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export const FeatureUsersPage = () => {
  const { feature } = useParams(); // Get feature from URL path
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showAddUser, setShowAddUser] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${apiUrl}/features/${feature}/users`;
      const { json } = await httpClient(url);
      setUsers(json);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [feature]);

  // Fetch users for the feature
  useEffect(() => {
    if (feature) {
      fetchUsers();
    }
  }, [feature, fetchUsers]);

  const addUserToFeature = async (userData) => {
    try {
      const url = `${apiUrl}/features/${feature}/users`;
      await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      showNotification('User added successfully', 'success');
      fetchUsers();
      setShowAddUser(false);
    } catch (err) {
      showNotification('Failed to add user: ' + err.message, 'error');
    }
  };

  const removeUserFromFeature = async (userId) => {
    try {
      const url = `${apiUrl}/features/${feature}/users/${userId}`;
      await httpClient(url, {
        method: 'DELETE',
      });
      showNotification('User removed successfully', 'success');
      fetchUsers();
    } catch (err) {
      showNotification('Failed to remove user: ' + err.message, 'error');
    }
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleBulkUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const url = `${apiUrl}/features/${feature}/users/bulk`;
      const { json } = await httpClient(url, {
        method: 'POST',
        body: formData,
      });
      
      showNotification(
        `Added ${json.addedUsers} users, skipped ${json.skippedUsers}`,
        'success'
      );
      fetchUsers();
    } catch (err) {
      showNotification('Failed to upload users: ' + err.message, 'error');
    }
  };

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const goBack = () => {
    window.location.href = '#/features';
  };

  if (!feature) {
    return (
      <Paper style={{ padding: 16, margin: 16 }}>
        <Typography variant="h6">No feature specified</Typography>
        <Typography>Invalid URL format</Typography>
      </Paper>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={goBack} style={{ marginRight: 8 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Users for Feature: {feature}
        </Typography>
      </Box>

      {/* Info Banner */}
      <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#d1ecf1', border: '1px solid #bee5eb' }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Public />
          <Typography>
            Managing users for feature: <strong>{feature}</strong>
            <br />
            <small>Only users listed here have access to this feature when it's not globally enabled.</small>
            <br />
            <small>If this feature is globally enabled, individual user management is not available.</small>
          </Typography>
        </Box>
      </Paper>

      {/* Actions */}
      <Box display="flex" style={{ gap: '16px', marginBottom: '16px' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowAddUser(true)}
        >
          Add User
        </Button>
        <input
          id="bulk-upload-input"
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleBulkUpload(e.target.files[0]);
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<GetApp />}
          onClick={() => document.getElementById('bulk-upload-input').click()}
        >
          Bulk Upload CSV
        </Button>
      </Box>

      {/* Users Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper style={{ padding: 16, backgroundColor: '#f8d7da', border: '1px solid #f5c6cb' }}>
          <Typography color="error">Error: {error}</Typography>
          <Button onClick={fetchUsers} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Social Provider</TableCell>
                <TableCell>Added Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">
                      No users found for this feature
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.phoneNumber}</TableCell>
                                         <TableCell>{user.user ? user.user.social : ''}</TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove User">
                        <IconButton
                          onClick={() => {
                            setUserToDelete(user);
                            setDeleteDialogOpen(true);
                          }}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add User Dialog */}
      <Dialog
        open={showAddUser}
        onClose={() => setShowAddUser(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add User to Feature: {feature}</DialogTitle>
        <DialogContent>
          <AddUserForm
            onSubmit={addUserToFeature}
            onCancel={() => setShowAddUser(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Remove User from Feature</DialogTitle>
        <DialogContent>
          <Typography>
            Remove "{userToDelete ? userToDelete.email : ''}" from feature "{feature}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => removeUserFromFeature(userToDelete?.id)}
            color="secondary"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
      />
    </div>
  );
};

// Add User Form Component  
const AddUserForm = ({ onSubmit, onCancel }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Fetch users when input changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (inputValue.length < 2) {
        setUsers([]);
        return;
      }
      
      setLoading(true);
      try {
        // First try with filtering
        let query = {
          sort: ['id', 'DESC'],
          page: 0,
          perPage: 50, // Fetch more users for client-side filtering fallback
          filter: { email: inputValue },
        };
        let url = `${apiUrl}/users?${stringify(query)}`;
        console.log('Search URL with filter:', url);
        
        let { json } = await httpClient(url);
        console.log('Search response:', json, 'Length:', json?.length);
        
        // If no results with exact filter, try without filter and filter client-side
        if (!json || json.length === 0) {
          console.log('No results with server filter, trying client-side filtering...');
          query = {
            sort: ['id', 'DESC'],
            page: 0,
            perPage: 100, // Get more users for client-side search
          };
          url = `${apiUrl}/users?${stringify(query)}`;
          console.log('Fallback URL without filter:', url);
          
          const response = await httpClient(url);
          const allUsers = Array.isArray(response.json) ? response.json : [];
          
          // Filter client-side by email containing the input
          json = allUsers.filter(user => 
            user.email && user.email.toLowerCase().includes(inputValue.toLowerCase())
          );
          console.log('Client-side filtered results:', json.length);
        }
        
        setUsers(Array.isArray(json) ? json : []);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchUsers, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleSubmit = () => {
    if (selectedUser && selectedUser.id) {
      onSubmit({ userId: selectedUser.id });
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setInputValue(`${user.email} (${user.social})`);
    setUsers([]); // Hide the list after selection
  };

  return (
    <Box style={{ padding: '16px 0' }}>
      <Typography variant="h6" gutterBottom>
        Select User to Add to Feature
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Search and select a user by their email and social provider.
      </Typography>
      
      <div style={{ position: 'relative' }}>
        <TextField
          label="Search Users"
          placeholder="Type to search by email..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setSelectedUser(null); // Clear selection when typing
          }}
          fullWidth
          margin="normal"
        />
        
        {loading && (
          <Box display="flex" justifyContent="center" style={{ marginTop: '8px' }}>
            <CircularProgress size={20} />
          </Box>
        )}
        
        {users.length > 0 && (
          <Paper style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: '200px', overflow: 'auto' }}>
            <List>
              {users.map((user) => (
                <ListItem 
                  key={user.id} 
                  button 
                  onClick={() => handleUserSelect(user)}
                  selected={selectedUser && selectedUser.id === user.id}
                >
                  <ListItemText
                    primary={`${user.email} (${user.social})`}
                    secondary={`${user.firstName} ${user.lastName}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
        
        {inputValue.length >= 2 && users.length === 0 && !loading && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
            No users found
          </Typography>
        )}
        
        {inputValue.length > 0 && inputValue.length < 2 && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
            Type at least 2 characters to search
          </Typography>
        )}
      </div>
      
      {selectedUser && (
        <Box style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <Typography variant="body2">
            <strong>Selected:</strong> {selectedUser.email} ({selectedUser.social})
            <br />
            <strong>Name:</strong> {selectedUser.firstName} {selectedUser.lastName}
          </Typography>
        </Box>
      )}
      
      <Box display="flex" justifyContent="flex-end" style={{ gap: '8px', marginTop: '24px' }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedUser}
        >
          Add User
        </Button>
      </Box>
    </Box>
  );
};

export default FeatureUsersPage; 